import { translate } from "app/language/service"
import { APP_CONTEXTS } from "app/model"
import { getStore } from "app/store/store"
import { refreshInventory } from "form/inventory/tools/common"
import { IForm, IFormDataLoader } from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { messageSVC } from "message/service"
import { ICampaignSspDeal, ISspDeal } from "ssp/model"
import { sspRPO } from "ssp/repository"
import { getForm } from "./common"
import { getScreensChanges } from "./screens"

export const refreshSSPDealList = async (appContext: APP_CONTEXTS, sspIds: number[]): Promise<boolean> => {
  if (sspIds.length === 0) {
    getStore().dispatch(formActionCreator.setAllSspDeals(appContext, []))
    return true
  }
  try {
    getStore().dispatch(formActionCreator.startLoadingAllSspDeals(appContext))
    const allSspDeals = await sspRPO.getAllSspDeals(sspIds)
    getStore().dispatch(formActionCreator.setAllSspDeals(appContext, allSspDeals))
    return true
  } catch (error) {
    console.error("Unable to refresh all ssp deals", error)
    getStore().dispatch(formActionCreator.setHasLoadingAllSspDealsError(appContext, true))
    return false
  }
}

export function noticeAndClearSspDealsError(form: IForm): void {
  messageSVC.error(translate("campaign.ssp-deal.error.selection"))
  getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { sspDealsError: false }))
}

export function synchronizeNewSspDealSelection(newSelectedSspDeals: number[] | ISspDeal[]): void {
  const form = getForm()
  getStore().dispatch(formActionCreator.startSynchronizingSspDeals(getForm().context.appContext))
  clearTimeout(state.sspDealsTimeout)
  const isNumberArray = newSelectedSspDeals.length > 0 && typeof newSelectedSspDeals[0] === "number"
  let selectedSspDeals: ICampaignSspDeal[] = isNumberArray
    ? []
    : newSelectedSspDeals.map(s => getCampaignSspDealFromSspDeal(form, s as ISspDeal))
  if (isNumberArray) {
    selectedSspDeals = form.screens.allSspDeals
      .filter(s => (newSelectedSspDeals as number[]).includes(s.id))
      .map(s => getCampaignSspDealFromSspDeal(form, s as ISspDeal))
  }
  state.sspDealsTimeout = setTimeout(() => {
    setCampaignSspDeals(selectedSspDeals)
  }, 800)
}

export const campaignSspDealFormDataLoader: IFormDataLoader = async (
  form: IForm,
  onFinish: (error?: string) => any
) => {
  try {
    const campaignSspDeals = await sspRPO.getCampaignSspDeals(form.tempCampaignId)
    form.context.relations.campaignSspDeals = campaignSspDeals
    form.screens.campaignSspDeals = campaignSspDeals.slice()
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Ssp-deals data-loader error ${String(error)}`)
  }
}

export const getCampaignSspDealId = (sspId: number, dealid: string): string => `${sspId}-${dealid}`

// @recursive
async function setCampaignSspDeals(newSelectedCampaignSspDeals: ICampaignSspDeal[]): Promise<void> {
  if (state.sspDealsIsSynchronizing) {
    state.sspDealsToApplySelection = newSelectedCampaignSspDeals
    return
  } else {
    state.sspDealsIsSynchronizing = true
  }
  const form = getForm()
  let hasError = false
  try {
    const newCampaignSspDealAssociations = await sspRPO.setCampaignSspDeals(
      form.tempCampaignId,
      newSelectedCampaignSspDeals
    )
    getStore().dispatch(
      formActionCreator.setCampaignSspDeals(
        form.context.appContext,
        getScreensChanges({
          ...form,
          screens: {
            ...form.screens,
            campaignSspDeals: newCampaignSspDealAssociations
          }
        }),
        newCampaignSspDealAssociations
      )
    )
  } catch (error) {
    console.error(error)
    hasError = true
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { sspDealsError: hasError }))
    getStore().dispatch(formActionCreator.stopSynchronizingSspDeal(form.context.appContext))
  }
  state.sspDealsIsSynchronizing = false
  if (state.sspDealsToApplySelection) {
    const toApplySspDealSelection = state.sspDealsToApplySelection
    state.sspDealsToApplySelection = null
    // @recursivity
    setCampaignSspDeals(toApplySspDealSelection)
  } else if (!hasError) {
    refreshInventory()
  }
}

const getCampaignSspDealFromSspDeal = (form: IForm, sspDeal: ISspDeal): ICampaignSspDeal => {
  const { campaignSspDeals } = form.screens
  const existingSspDeal = campaignSspDeals.find(s => s.dealid === sspDeal.dealid)
  if (existingSspDeal) {
    return existingSspDeal
  }
  return {
    campaign_id: form.tempCampaignId,
    ssp_deal_id: sspDeal.id,
    ssp_id: sspDeal.ssp_id,
    dealid: sspDeal.dealid,
    bids: 0,
    clicks: 0,
    impressions: 0,
    status_id: "active",
    id: undefined as any
  }
}
